import { supabase, PROFILES_TABLE, isSupabaseConnected } from './supabase';
import { localAuth } from './storage';

export const getAuthMode = () => isSupabaseConnected ? 'supabase' : 'local';

// ─── Uniqueness Checks ───────────────────────────────────────────────────────

export const checkEmailExists = async (email) => {
  if (!isSupabaseConnected) return false;
  if (!email) return false;

  const { data, error } = await supabase
    .from(PROFILES_TABLE)
    .select('id')
    .eq('email', email.toLowerCase().trim())
    .maybeSingle();

  if (error) {
    console.warn('checkEmailExists error:', error.message);
    return false;
  }
  return !!data;
};

export const checkMobileExists = async (mobile) => {
  if (!isSupabaseConnected) return false;
  if (!mobile) return false;

  const normalized = mobile.replace(/[\s-]/g, '');

  const { data, error } = await supabase
    .from(PROFILES_TABLE)
    .select('id')
    .eq('mobile', normalized)
    .maybeSingle();

  if (error) {
    console.warn('checkMobileExists error:', error.message);
    return false;
  }
  return !!data;
};

// ─── Auth ─────────────────────────────────────────────────────────────────────

export const authSignUp = async ({ email, password, fullName, mobile, country, monthlyVolume, platforms }) => {
  if (!isSupabaseConnected) {
    return localAuth.signUp({ email, password, fullName, mobile, country, monthlyVolume, platforms });
  }

  const normalizedMobile = mobile ? mobile.replace(/[\s-]/g, '') : '';
  const normalizedEmail = email.toLowerCase().trim();

  const { data, error } = await supabase.auth.signUp({
    email: normalizedEmail,
    password,
    options: {
      data: {
        full_name: fullName || '',
        mobile: normalizedMobile,
        country: country || '',
        monthly_volume: monthlyVolume || '',
        platforms: platforms || [],
      },
    },
  });

  if (error) {
    const msg = error.message?.toLowerCase() || '';
    if (
      msg.includes('already registered') ||
      msg.includes('already exists') ||
      msg.includes('email already')
    ) {
      return { data: null, error: { message: 'DUPLICATE_EMAIL' } };
    }
    return { data: null, error };
  }

  if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
    return { data: null, error: { message: 'DUPLICATE_EMAIL' } };
  }

  if (data?.user) {
    const { error: profileError } = await supabase
      .from(PROFILES_TABLE)
      .upsert({
        id: data.user.id,
        email: normalizedEmail,
        full_name: fullName || '',
        mobile: normalizedMobile,
        country: country || '',
        monthly_volume: monthlyVolume || '',
        platforms: platforms || [],
        credits: 100,
      }, { onConflict: 'id', ignoreDuplicates: false });

    if (profileError) {
      if (profileError.message?.includes('user_profiles_mobile_unique') ||
          profileError.code === '23505') {
        return { data: null, error: { message: 'DUPLICATE_MOBILE' } };
      }
      console.warn('Profile upsert warning:', profileError.message);
    }
  }

  return { data, error: null };
};

export const authSignIn = async ({ email, password }) => {
  if (!isSupabaseConnected) {
    return localAuth.signIn({ email, password });
  }
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { data, error };
};

export const authSignOut = async () => {
  if (!isSupabaseConnected) {
    return localAuth.signOut();
  }
  const { error } = await supabase.auth.signOut();
  return { error };
};

export const getSession = async () => {
  if (!isSupabaseConnected) {
    return localAuth.getSession();
  }
  const { data, error } = await supabase.auth.getSession();
  return { data, error };
};

export const getUser = async () => {
  if (!isSupabaseConnected) {
    return localAuth.getUser();
  }
  const { data, error } = await supabase.auth.getUser();
  return { data, error };
};

export const getCredits = async (userId) => {
  if (!isSupabaseConnected) {
    return localAuth.getCredits();
  }
  if (!userId) return 0;

  const { data, error } = await supabase
    .from(PROFILES_TABLE)
    .select('credits')
    .eq('id', userId)
    .single();

  if (error) {
    console.warn('getCredits error:', error.message);
    return 0;
  }
  return data?.credits ?? 0;
};

export const deductCredit = async (userId) => {
  if (!isSupabaseConnected) {
    return localAuth.deductCredit();
  }
  if (!userId) return false;

  const { data, error } = await supabase.rpc('deduct_credit_safe', { user_id: userId });

  if (error) {
    console.warn('deductCredit RPC error, falling back:', error.message);
    const current = await getCredits(userId);
    if (current <= 0) return false;
    const { error: updateError } = await supabase
      .from(PROFILES_TABLE)
      .update({ credits: current - 1, updated_at: new Date().toISOString() })
      .eq('id', userId)
      .gt('credits', 0);
    return !updateError;
  }

  return data === true;
};

export const addCredits = async (userId, amount) => {
  if (!isSupabaseConnected) {
    return localAuth.addCredits(amount);
  }
  if (!userId) return false;

  const current = await getCredits(userId);
  const newTotal = current + amount;

  const { error } = await supabase
    .from(PROFILES_TABLE)
    .update({ credits: newTotal, updated_at: new Date().toISOString() })
    .eq('id', userId);

  if (error) {
    console.warn('addCredits error:', error.message);
    return false;
  }
  return newTotal;
};

// ─── Change Password ──────────────────────────────────────────────────────────

export const changePassword = async (currentPassword, newPassword) => {
  if (!isSupabaseConnected) {
    return { error: { message: 'Password change is only supported with Supabase.' } };
  }

  // Step 1: Get the currently logged-in user's email
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError || !userData?.user?.email) {
    return { error: { message: 'Unable to retrieve current user. Please log in again.' } };
  }

  // Step 2: Re-authenticate with the current password to verify identity
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: userData.user.email,
    password: currentPassword,
  });

  if (signInError) {
    return { error: { message: 'Current password is incorrect.' } };
  }

  // Step 3: Update to the new password
  const { data, error } = await supabase.auth.updateUser({ password: newPassword });
  return { data, error };
};

// ─── Record a transaction after successful payment ────────────────────────────
export const recordTransaction = async ({ userId, planName, paymentId, creditsPurchased, amountSpent }) => {
  if (!isSupabaseConnected) {
    console.warn('recordTransaction: Supabase not connected, skipping.');
    return false;
  }
  if (!userId) {
    console.warn('recordTransaction: no userId provided.');
    return false;
  }

  const { error } = await supabase
    .from('transactions')
    .insert({
      user_id: userId,
      plan_name: planName,
      payment_id: paymentId,
      credits_purchased: creditsPurchased,
      amount_spent: amountSpent,
      date: new Date().toISOString(),
      status: 'success',
    });

  if (error) {
    console.error('recordTransaction error:', error.message, '| code:', error.code);
    return false;
  }

  console.log('Transaction recorded successfully for user:', userId);
  return true;
};

export const onAuthStateChange = (callback) => {
  if (!isSupabaseConnected) {
    return localAuth.onAuthStateChange(callback);
  }
  return supabase.auth.onAuthStateChange(callback);
};